import React, { useRef, useState } from "react";
import { notification, Popconfirm, Slider, Space, Tooltip } from "antd";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { workforceDataUpdateResponse } from "../../carecoordinator/workforce/AddEditWorkforceModal";
import { EventType } from "../../../service/network/utils/EventType";
import { executeIntent } from "../../utils/IntentExecutor";
import { convertDateToEODTimestamp } from "../../../utils/dateutils";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import Dropzone from "react-dropzone";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import AvatarEditor from "react-avatar-editor";
import base64 from "base-64";
import apiService from "../../../service/apiservice/apiService";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";

const VolunteerProfileAction = ({ userDetails, showDelete, showUpload }) => {
  const user = useReadMemory(MemoryType.USER);
  const editorRef = useRef(null);
  const configuration = useRecoilValue(currentConfiguration);
  const bucketName =
    configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const updateVolunteer = async (profilePicture) => {
    const payload = {
      data: {
        ...userDetails,
        profilePicture,
        birthDate: convertDateToEODTimestamp(userDetails?.birthDate),
        anniversary: convertDateToEODTimestamp(userDetails?.anniversary),
        creatorIdentifier: user?.userIdentifier,
      },
    };
    try {
      const response = await executeIntent(
        IntentType.UPDATE_WORKFORCE,
        payload,
      );
      workforceDataUpdateResponse.next({
        type: EventType.WORKFORCE_DATA_UPDATED,
      });
      if (response?.identifier) {
        notification.success({
          message: profilePicture
            ? "Your profile picture has been updated successfully."
            : "Your profile picture has been removed.",
          placement: "bottomRight",
        });
      } else {
        notification.error({
          message: profilePicture
            ? "We couldn’t upload your profile picture. Please try again."
            : "We couldn’t remove your profile picture. Please try again.",
          placement: "bottomRight",
        });
      }
    } catch (err) {
      notification.error({
        message: "Something went wrong. Please try again.",
        placement: "bottomRight",
      });
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await updateVolunteer("");
    } finally {
      setLoading(false);
    }
  };

  const handleFileDrop = (acceptedFiles) => {
    setImage(acceptedFiles[0]);
    setIsModalOpen(true);
  };

  const handleUpload = async () => {
    if (editorRef.current) {
      try {
        setLoading(true);
        const canvas = editorRef.current.getImageScaledToCanvas();

        // Convert canvas to Base64 string
        const base64Data = canvas.toDataURL("image/png");

        // Send JSON payload instead of FormData
        const response = await apiService.post(
          `event/api/v1/store/object/json/upload?bucketName=${bucketName?.value}`,
          {
            object: base64.encode(base64Data),
            extension: "json",
          },
        );

        if (response?.objectId) {
          await updateVolunteer(response?.objectId);
        } else {
          notification.error({
            message: "Something went wrong",
            placement: "bottomRight",
          });
        }
      } catch (err) {
        notification.error({
          message: "Upload failed",
          placement: "bottomRight",
        });
      } finally {
        setLoading(false);
        setIsModalOpen(false);
      }
    }
  };

  const footerButtons = (
    <>
      <SecondaryButton
        buttonText="Cancel"
        handleOnClick={() => setIsModalOpen(false)}
      />
      <PrimaryButton
        buttonText="Upload"
        handleOnClick={handleUpload}
        loading={loading}
      />
    </>
  );

  return (
    <>
      <Space style={{ display: "flex", justifyContent: "center" }}>
        {showUpload && (
          <Dropzone
            onDrop={handleFileDrop}
            accept={{ "image/*": [] }}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Tooltip placement="bottom" title="Upload Profile Picture">
                  <UploadIcon
                    style={{ color: "var(--secondary-theme-color)" }}
                    className="cursor-pointer"
                  />
                </Tooltip>
              </div>
            )}
          </Dropzone>
        )}
        {showDelete && (
          <Popconfirm
            title="Are you sure you want to delete profile picture?"
            onConfirm={handleConfirm}
            okText="OK"
            cancelText="Cancel"
            placement="right"
            overlayClassName="article-delete-popconfirm"
            okButtonProps={{ loading: loading }}
          >
            <Tooltip placement="bottom" title="Delete Profile Picture">
              <DeleteIcon
                style={{ color: "var(--secondary-theme-color)" }}
                className="cursor-pointer"
              />
            </Tooltip>
          </Popconfirm>
        )}
      </Space>

      <CustomModal
        isModalOpen={isModalOpen}
        footer={footerButtons}
        closeModal={setIsModalOpen}
        closable={false}
      >
        <>
          <Space
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <AvatarEditor
              ref={editorRef}
              image={image}
              width={250}
              height={250}
              scale={scale}
              rotate={0}
              style={{ display: "flex", justifyContent: "center" }}
            />

            <div style={{ padding: 8, width: 250 }}>
              <p style={{ marginBottom: 8, textAlign: "center" }}>Zoom</p>
              <Slider
                min={1}
                max={2}
                step={0.1}
                value={scale}
                onChange={(value) => setScale(value)}
              />
            </div>
          </Space>
        </>
      </CustomModal>
    </>
  );
};

export default VolunteerProfileAction;
